import firebase from 'firebase/app';
import { router } from '@/router';
import * as enums from '@/types/enums';
import {arrayUnion, arrayRemove} from 'vuex-easy-firestore';
import { NotifySubscriberPayloadInterface } from '@/types/interfaces';

export default {

  /** Main Module */
  mainModuleDecorator: (module: any): void => {
    module.firestorePath = 'consenz/{version}';
    // The path to a "collection" or single "document" in firestore.
    // You can use `{userId}` which will be replaced with the user Id.
    module.firestoreRefType = 'doc';
    // `'collection'` or `'doc'`. Depending on your `firestorePath`.

    module.actions.initStore = async ({ state, dispatch, commit, rootGetters }) => {

      // check this: in democrasee project, this code block does not exist
      const firestore = firebase.firestore();
      const settings = {};
      console.log('mainModule.ts Init Store. Set firestore settings: ' + JSON.stringify(settings));
      firestore.settings(settings);

      try {
        console.log('mainModule.ts Dispatch openDBChannel');
        await dispatch('openDBChannel', { version: 'v1' }).catch((error) => {
          if (error === 'preventInitialDocInsertion') {
            console.log('mainModule.ts ERROR IN openDBChannel OF CONSENZ:', error);
            return;
          }
        });
        await dispatch('documentsModule/fetchById', state.prettyLink, { root: true });
        console.log('mainModule.ts Dispatch sectionsModule/openDBChannel');
        await dispatch('sectionsModule/openDBChannel', { where: [['documentId', '==', state.prettyLink]] }, { root: true });
        console.log('mainModule.ts Dispatch argumentsModule/openDBChannel');
        await dispatch('argumentsModule/openDBChannel', { where: [['documentId', '==', state.prettyLink]] }, { root: true });
        await dispatch('commentsModule/openDBChannel', { where: [['documentId', '==', state.prettyLink]] }, { root: true });
        await dispatch('changeEventsModule/openDBChannel', { where: [['documentId', '==', state.prettyLink]] }, { root: true });
        await dispatch(
          'usersModule/openDBChannel',
          {
            where: [['documents', 'array-contains', state.prettyLink]],
          },
          { root: true },
        );
      } catch (error) {
        console.error(error);
      } finally {
        commit('setLoading', false);
        commit('documentsModule/setPrettyLink', state.prettyLink, { root: true });
        const payload = { currentRoute: router.currentRoute };
        console.log('mainModule.ts Commit setToState ' + payload);
        commit('routerModule/setToState', payload, { root: true });
      }
    }
  },

  /** Users Module */
  usersModuleDecorator: (module: any): void => {
    module.firestorePath = 'users';
    module.firestoreRefType = 'collection';

    /**
     * updates the current user
     */
    module.actions.updateCurrentUser = async ({ state, dispatch }, { updateObject }) => {
      await dispatch('patch', { id: state.currentUser.uid, ...updateObject });
    };
  },

  /** Documents Module */
  documentsModuleDecorator: (module: any): void => {
    module.firestorePath = 'documents';
    module.firestoreRefType = 'collection';

    module.getters.documentSendNotifications = (state) => {
      const prettyLink = state.prettyLink;
      const data = state.data;
      const datum = data[prettyLink];
      if (datum == null) {
        return datum;
      }
      return datum.sendNotifications;
    }
  },

  /** Sections Module */
  sectionsModuleDecorator: (module: any): void => {
    module.firestorePath = 'sections';
    module.firestoreRefType = 'collection';
    /**
     * gets the size of the sections in a specific status
     * @param {number} status
     */
    module.actions.getSizeByStatus = async ({
      state, rootGetters, dispatch, getters, commit
    }, status) => {
      if (state.fetchedSize[status]) {
        return getters['sizeByStatus'](status);
      } else {
        const where = [['documentId', '==', rootGetters['mainModule/prettyLink']], ['status', '==', status]];
        const querySnapshot = await dispatch('fetch', {
          where,
        });
        const size = querySnapshot.size !== undefined ? querySnapshot.size : 0;
        commit('setToSize', { status, size });
        commit('fetchedSize', { status });
        return size;
      }
    }
    /**
     * updates the section
     * @param {string} id the id of the section
     * @param {object} updateObject the keys and values to update
     */
    module.actions.updateSection = async ({dispatch}, {id, updateObject}) => {
      dispatch('patch', { id, ...updateObject });
    };
  },

  /** Arguments Module */
  argumentsModuleDecorator: (module: any): void => {
    module.firestorePath = 'arguments';
    module.firestoreRefType = 'collection';

    /**
     * adds user the convinced array of the argument
     * @param {string} id argument id
     */
    module.actions.userConvinced = async ({ dispatch, rootGetters }, id) => {
      const ownerUid = rootGetters['usersModule/userUid'];
      return await dispatch('patch', { id, convinced: arrayUnion(ownerUid) });
    },
    /**
     * deleted user the convinced array of the argument
     * @param {string} id argument id
     */
    module.actions.userUnconvinced = async ({ dispatch, rootGetters }, id) => {
      const ownerUid = rootGetters['usersModule/userUid'];
      return await dispatch('patch', { id, convinced: arrayRemove(ownerUid) });
    }
  },

  /** Comments Module */
  commentsModuleDecorator: (module: any): void => {
    module.firestorePath = 'comments';
    module.firestoreRefType = 'collection';
  },

  /** ChangeEvents Module */
  changeEventsModuleDecorator: (module: any): void => {
    module.firestorePath = 'changeEvents';
    module.firestoreRefType = 'collection';
  },

  /** Voting Module */
  votingModuleDecorator: (module: any): void => {
    module.firestorePath = '';
    module.firestoreRefType = '';

    /**
   * ends the voting of the section because ite reached threshold
   * sending notifications
   * navigation to success page
   * @param {VotesInterface} updatedVotes
   * @param {SectionInterface} section
   * @param {string} parentSectionId
   * @param {number} sectionIndex
   */
    module.actions.endVoting = async ({ dispatch, commit, rootGetters }, { section, parentSectionId, sectionIndex, sendNotifications }) => {
      // Check: if a notification should be sent on ending vote
      // if (sendNotifications) {
      //   const notificationPayload: NotifySubscriberPayloadInterface = {
      //     sectionOrArgumentContent: section.content,
      //     documentId: rootGetters['documentsModule/documentId'],
      //     sectionId: section.id,
      //     trigger: enums.NOTIFICATION_TRIGGER.sectionApproval,
      //   }
      //   await dispatch('notificationsModule/notifySubscribers', notificationPayload)
      // }
      commit('displayModule/showSnackbar', {}, {root: true})
    }
  },

  displayModuleDecorator: (module: any): void => {
    module.firestorePath = '';
    module.firestoreRefType = '';
  },

  routerModuleDecorator: (module: any): void => {
    module.firestorePath = '';
    module.firestoreRefType = '';
  }
}



